// Maps auth/network/permission failures to user-facing messages, so a network
// problem isn't reported as wrong login details and raw Firebase/Firestore
// error strings never reach hosts.
//
// Pure module (node-testable, scripts/authErrors.test.mjs) apart from the
// optional navigator.onLine hint, which callers can inject for tests.
// Ported from guestlist-web src/utils/authErrors.js and extended.

export const NETWORK_ERROR_MESSAGE = "Can't reach the server. Check your internet connection and try again.";

export const TOO_MANY_ATTEMPTS_MESSAGE = 'Too many attempts. Please wait a minute and try again.';

export const NO_ACCESS_MESSAGE = "Your login doesn't have access to this. Try signing out and back in, or speak with management.";

const NETWORK_ERROR_CODES = new Set([
  'auth/network-request-failed', // Firebase Auth: request never reached the server
  'auth/timeout',
  'unavailable', // Firestore: backend unreachable
  'functions/unavailable',
  'deadline-exceeded',
  'functions/deadline-exceeded'
]);

const WRONG_LOGIN_CODES = new Set(['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found', 'auth/invalid-login-credentials']);

function isOffline(onLine) {
  if (typeof onLine === 'boolean') return !onLine;
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function errorCode(err) {
  return String(err?.code || '');
}

// A fetch() that never reached the server rejects with TypeError('Failed to
// fetch') (or 'Load failed' on Safari/WebKit — e.g. the Instagram in-app browser).
function isFetchNetworkError(err) {
  if (!err) return false;
  const msg = String(err.message || '');
  return err.name === 'TypeError' && (msg.includes('Failed to fetch') || msg.includes('Load failed') || msg.includes('NetworkError'));
}

/**
 * Returns the message to show for a failed sign-in (or the post-login profile
 * read). `fallback` is the message for genuine auth failures — wrong email or
 * password stays exactly as before.
 */
export function authErrorMessage(err, fallback, onLine) {
  if (isOffline(onLine)) return NETWORK_ERROR_MESSAGE;

  const code = errorCode(err);
  if (NETWORK_ERROR_CODES.has(code) || isFetchNetworkError(err)) return NETWORK_ERROR_MESSAGE;
  if (code === 'auth/too-many-requests') return TOO_MANY_ATTEMPTS_MESSAGE;
  return fallback;
}

/**
 * Full mapping for email/password sign-in forms: wrong credentials, disabled
 * accounts, rate limits and network failures all get plain-English messages.
 * Never returns a raw Firebase error string.
 */
export function signInErrorMessage(err, onLine) {
  if (isOffline(onLine)) return NETWORK_ERROR_MESSAGE;

  const code = errorCode(err);
  if (NETWORK_ERROR_CODES.has(code) || isFetchNetworkError(err)) return NETWORK_ERROR_MESSAGE;
  if (WRONG_LOGIN_CODES.has(code)) return 'Incorrect email or password.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email address.';
  if (code === 'auth/user-disabled') return 'This account has been disabled. Please speak with management.';
  if (code === 'auth/too-many-requests') return TOO_MANY_ATTEMPTS_MESSAGE;
  return 'Unable to sign in. Please try again.';
}

/**
 * Generic mapping for data loads and saves (Firestore listeners/queries,
 * callables, fetch): offline, unreachable backend, missing permissions and
 * rate limits become plain English; anything else gets `fallback`.
 *
 * Server-authored messages (HttpsError from our own functions) usually ARE the
 * friendly text, so callers that want them should pass
 * `friendlyLoadError(err, serverMessageOr(err, 'fallback…'))`.
 */
export function friendlyLoadError(err, fallback, onLine) {
  if (isOffline(onLine)) return NETWORK_ERROR_MESSAGE;

  const code = errorCode(err);
  if (NETWORK_ERROR_CODES.has(code) || isFetchNetworkError(err)) return NETWORK_ERROR_MESSAGE;
  if (code === 'permission-denied' || code === 'functions/permission-denied') return NO_ACCESS_MESSAGE;
  if (code === 'auth/too-many-requests' || code === 'resource-exhausted' || code === 'functions/resource-exhausted') {
    return TOO_MANY_ATTEMPTS_MESSAGE;
  }
  return fallback || 'Something went wrong. Please try again.';
}

/**
 * Returns a server-authored message when the error carries one that is safe to
 * show (our callables throw HttpsError with host-friendly text), otherwise the
 * fallback. Raw Firebase SDK strings ("Firebase: Error (auth/...)") and
 * Firestore permission text are never passed through.
 */
export function serverMessageOr(err, fallback) {
  const msg = String(err?.message || '').trim();
  if (!msg) return fallback;
  if (msg.startsWith('Firebase:') || msg.includes('Missing or insufficient permissions')) return fallback;
  if (errorCode(err).startsWith('auth/')) return fallback;
  return msg;
}
